// Opens the one well a jump or a well list input found: selects it and opens
// its card at once.
import type mapboxgl from "mapbox-gl";
import type { Dispatch } from "redux";
// eslint-disable-next-line no-restricted-imports -- the same intentional dual-write useMapClickSelection makes, for cross-tab redux-state-sync
import { setSelectedWells as setSelectedWellsRedux } from "store/features/map/mapSlice";
import type { ISelectedWell } from "stores/map";
import { useMapStore } from "stores/map";
import { useWellCardPanelStore } from "stores/wellCardPanel";

import { fetchPerpendicularLine } from "api/map";

// One well fits to a point, so the cap alone sets its zoom.
export const SINGLE_WELL_MAX_ZOOM = 13;

// The camera waits on this request, so a slow answer falls back to the box.
const MIDPOINT_TIMEOUT_MS = 1500;

/**
 * Orders single-well opens and jumps ACROSS surfaces: the Well tab, the Ctrl+A
 * dialogue, the well list input and each map widget. A slower earlier one
 * would otherwise land last and move the camera the user has moved on from.
 */
let latestWellOpen = 0;

/** Supersedes every earlier open and returns the new one's token. */
export function claimWellOpen(): number {
  return ++latestWellOpen;
}

export function isLatestWellOpen(token: number): boolean {
  return token === latestWellOpen;
}

/**
 * The point the XDA section line passes through. The jump's box is built from
 * bottom holes, so a horizontal well would otherwise centre on its toe. Null
 * on any failure, such as the 400 for a well with no survey.
 */
export async function wellMidpoint(
  uwi: string,
  signal?: AbortSignal
): Promise<Nullable<[number, number]>> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort);
  let timer: Maybe<ReturnType<typeof setTimeout>>;
  const timedOut = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      abort();
      resolve(null);
    }, MIDPOINT_TIMEOUT_MS);
  });
  try {
    const line = await Promise.race([
      fetchPerpendicularLine(uwi, undefined, controller.signal),
      timedOut
    ]);
    const [a, b] = Array.isArray(line) ? line : [];
    if (![a?.x, a?.y, b?.x, b?.y].every(Number.isFinite)) return null;
    return [(a.x + b.x) / 2, (a.y + b.y) / 2];
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}

/**
 * Whether the user can see the map. A dashboard with no map widget still mounts
 * one with no size, and a widget shown fullscreen covers the grid and marks it
 * inert. The map's own fullscreen takes it out of the grid.
 *
 * Measured the way mapbox sizes its canvas. Mapbox gives an empty container a
 * 400x300 canvas, so a hidden camera still moves and projects.
 */
export function isMapOnScreen(map: mapboxgl.Map): boolean {
  const container = map.getContainer();
  const { width, height } = container.getBoundingClientRect();
  return width > 0 && height > 0 && !container.closest("[inert]");
}

export function selectAndOpenWell(uwi: string, dispatch: Dispatch): ISelectedWell {
  // All we know about the well. The data table's row click selects with the
  // same record.
  const well = { Uwi: uwi } as ISelectedWell;
  const selection = { [uwi]: well };
  useMapStore.getState().setSelectedWells(selection);
  dispatch(setSelectedWellsRedux(selection));
  const panel = useWellCardPanelStore.getState();
  panel.setTargetUwi(uwi);
  panel.setOpen(true);
  // The fit does not close the popup, which would stay up on the previous well.
  panel.setPreview(null);
  return well;
}
